"use client";

import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { Loader2, Package } from "lucide-react";
import { useFilterProductSKUs } from "@/hooks/useProductSKU";
import { ProductSKUDetailDialog } from "./ProductSKUDetailDialog";
import { useState } from "react";

interface ProductCategoryGridProps {
  brandId: string;
  category?: string | null;
}

export function ProductCategoryGrid({
  brandId,
  category,
}: ProductCategoryGridProps) {
  const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
  const { ref, inView } = useInView();

  const categories = category ? [category] : null;
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFilterProductSKUs(brandId, categories);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const items = data?.pages.flat() ?? [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-3">
        <Package className="h-10 w-10 opacity-40" />
        <p className="text-sm">No products found in this category.</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 p-4">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex flex-col overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedSkuId(item.id)}
          >
            <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
              {item.product_url ? (
                <img
                  src={item.product_url}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Package className="h-10 w-10 text-gray-300" />
              )}
            </div>
            <div className="px-2 py-1.5">
              <p className="text-xs font-medium text-gray-800 truncate">
                {item.name}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={ref} className="flex justify-center py-4">
        {isFetchingNextPage && (
          <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
        )}
      </div>

      <ProductSKUDetailDialog
        brandId={brandId}
        skuId={selectedSkuId}
        onClose={() => setSelectedSkuId(null)}
      />
    </>
  );
}
